import { useEffect, useState, useMemo } from "react";
import { createClient } from "@supabase/supabase-js";
import {
    Calendar,
    dateFnsLocalizer,
    Views,
    Navigate,
} from "react-big-calendar";
import {
    format,
    parse,
    startOfWeek,
    getDay,
    addDays,
    addWeeks,
    addMonths,
    subDays,
    subWeeks,
    subMonths,
} from "date-fns";
import { es } from "date-fns/locale";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Button, ButtonGroup } from "react-bootstrap";

const { REACT_APP_SUPABASE_URL, REACT_APP_SUPABASE_KEY } = process.env;
const supabase = createClient(REACT_APP_SUPABASE_URL, REACT_APP_SUPABASE_KEY);

const locales = { es };
const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
    getDay,
    locales,
});

const CustomToolbar = ({ label, onView, view, date, setDate }) => {
    const goToBack = () => {
        if (view === Views.MONTH) setDate(subMonths(date, 1));
        else if (view === Views.WEEK || view === Views.AGENDA) setDate(subWeeks(date, 1));
        else if (view === Views.DAY) setDate(subDays(date, 1));
    };

    const goToNext = () => {
        if (view === Views.MONTH) setDate(addMonths(date, 1));
        else if (view === Views.WEEK || view === Views.AGENDA) setDate(addWeeks(date, 1));
        else if (view === Views.DAY) setDate(addDays(date, 1));
    };

    const goToToday = () => setDate(new Date());

    return (
        <div
            style={{
                marginBottom: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
            }}
        >
            <h5 style={{ margin: 0 }}>{label}</h5>
            <div>
                <ButtonGroup className="me-2">
                    <Button type="button" size="sm" onClick={goToBack}>
                        Anterior
                    </Button>
                    <Button
                        style={{ backgroundColor: "#007bff", color: "#fff" }}
                        type="button"
                        size="sm"
                        onClick={goToToday}
                    >
                        Hoy
                    </Button>
                    <Button type="button" size="sm" onClick={goToNext}>
                        Siguiente
                    </Button>
                </ButtonGroup>
                <ButtonGroup>
                    <Button type="button" size="sm" onClick={() => onView(Views.MONTH)}>
                        Mes
                    </Button>
                    <Button type="button" size="sm" onClick={() => onView(Views.WEEK)}>
                        Semana
                    </Button>
                    <Button type="button" size="sm" onClick={() => onView(Views.DAY)}>
                        Día
                    </Button>
                    <Button type="button" size="sm" onClick={() => onView(Views.AGENDA)}>
                        Agenda
                    </Button>
                </ButtonGroup>
            </div>
        </div>
    );
};

const AdminAgendaCitas = () => {
    const [citas, setCitas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState(Views.WEEK);
    const [date, setDate] = useState(new Date());

    const fetchCitas = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from("citas")
            .select("*")
            .order("hora", { ascending: true });

        if (error) {
            alert("Error al cargar citas: " + error.message);
        } else {
            setCitas(data || []);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchCitas();
    }, []);

    // ✅ MODIFICACIÓN: filtramos citas que contengan "fict"
    const events = useMemo(
        () =>
            (citas || [])
                .filter((c) => {
                    const textoCompleto = `
                        ${c.nombre || ""}
                        ${c.primer_apellido || ""}
                        ${c.tipo_de_masaje || ""}
                    `.toLowerCase();

                    return !textoCompleto.includes("fict");
                })
                .map((c) => {
                    const start = new Date(c.hora);
                    const end = new Date(start.getTime() + (c.duracion || 60) * 60000);

                    return {
                        id: c.id,
                        title: `${c.nombre || ""} ${c.primer_apellido || ""} (${c.tipo_de_masaje || "Masaje"})`.trim(),
                        start,
                        end,
                        allDay: false,
                        resource: c,
                    };
                }),
        [citas]
    );

    const onSelectEvent = (event) => {
        const { resource } = event;
        alert(
            `Cita:\n${event.title}\n${event.start.toLocaleString()}\n` +
            `Tel: ${resource?.telefono || "-"}\nEmail: ${resource?.email || "-"}`
        );
    };

    return (
        <div style={{ margin: "20px" }}>
            <h3 style={{ fontSize: "2rem", marginTop: "1.5rem" }}>
                Agenda de Citas
            </h3>
            {loading ? (
                <p>Cargando citas...</p>
            ) : (
                <Calendar
                    localizer={localizer}
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    date={date}
                    onNavigate={setDate}
                    view={view}
                    onView={setView}
                    step={15}
                    timeslots={4}
                    style={{ height: "100%" }}
                    views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
                    components={{
                        toolbar: (props) => (
                            <CustomToolbar
                                {...props}
                                date={date}
                                setDate={setDate}
                                view={view}
                            />
                        ),
                    }}
                    messages={{
                        next: "Sig.",
                        previous: "Ant.",
                        today: "Hoy",
                        month: "Mes",
                        week: "Semana",
                        day: "Día",
                        agenda: "Agenda",
                        noEventsInRange: "No hay eventos en este rango.",
                        date: "Fecha",
                        time: "Hora",
                        event: "Cita",
                    }}
                />
            )}
        </div>
    );
};

export default AdminAgendaCitas;